import type { JsonLd } from '../lib/seo';
import { Icon } from './Icon';

export type FaqItem = { question: string; answer: string };

/** FAQPage structured data. Only use for questions that are visible on the page. */
export function faqLd(items: FaqItem[]): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };
}

/**
 * Accessible accordion built on <details>/<summary>: works without JS, keyboard friendly,
 * and the answers stay in the prerendered HTML for crawlers.
 * `headingLevel` sets the level of each question so the page's heading order stays valid.
 */
export function Faq({ items, headingLevel = 'h3' }: { items: FaqItem[]; headingLevel?: 'h3' | 'h4' }) {
  const Heading = headingLevel;
  return (
    <div className="flex flex-col gap-space-sm">
      {items.map((f) => (
        <details key={f.question} className="group rounded bg-surface-cream border border-transparent open:border-gold-light open:bg-surface-ivory transition-colors">
          <summary className="flex items-center justify-between gap-space-md cursor-pointer list-none px-space-md md:px-space-lg py-space-md [&::-webkit-details-marker]:hidden">
            <Heading className="font-headline-sm text-[18px] leading-7 font-normal text-primary">{f.question}</Heading>
            <Icon name="keyboard_arrow_down" size={22} className="text-secondary transition-transform duration-300 group-open:rotate-180" />
          </summary>
          <div className="px-space-md md:px-space-lg pb-space-md font-body-md text-body-md text-on-surface-variant">
            <p>{f.answer}</p>
          </div>
        </details>
      ))}
    </div>
  );
}
